import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, RefreshCw, ShoppingBag, Eye, X } from 'lucide-react';

const ORDER_STATUSES = [
  { value: 'PENDING', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'CONFIRMED', label: 'Confirmed', color: 'bg-blue-100 text-blue-800' },
  { value: 'DISPATCHED', label: 'Dispatched', color: 'bg-green-100 text-green-800' },
  { value: 'DELIVERED', label: 'Delivered', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
];

const SalesOrders = () => {
  const { api, isAdmin, hasRole } = useAuth();
  const [data, setData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [parts, setParts] = useState([]);
  const [partsInventory, setPartsInventory] = useState([]);
  const [activePOs, setActivePOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [orderDetails, setOrderDetails] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);

  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);

  const canEdit = isAdmin() || hasRole(['ADMIN', 'DESPATCH']);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Load sequentially to avoid SQL Server connection contention
      const ordersRes = await api.get('/sales-orders');
      const posRes = await api.get('/customer-pos/active-for-sales');
      setData(ordersRes.data);
      setActivePOs(posRes.data);
      setLoading(false);
      // Load reference data after main data renders
      const [custRes, partsRes, invRes] = await Promise.all([
        api.get('/customers'),
        api.get('/parts'),
        api.get('/parts/inventory-check')
      ]);
      setCustomers(custRes.data);
      setParts(partsRes.data);
      setPartsInventory(invRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getCustName = (id) => {
    if (!id) return '-';
    const c = customers.find(x => x.id === id);
    return c ? `${c.code} - ${c.name}` : `ID:${id}`;
  };
  const getPartName = (id) => {
    const p = parts.find(x => x.id === id);
    return p ? `${p.part_no} - ${p.name}` : `ID:${id}`;
  };
  const getPartInventory = (id) => {
    const inv = partsInventory.find(p => p.id === id);
    return inv ? inv.available_stock : 0;
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setSelectedPO(null);
      setFormData({
        order_date: item.order_date || '',
        customer_id: (item.customer_id ? item.customer_id.toString() : ''),
        customer_po_id: (item.customer_po_id ? item.customer_po_id.toString() : ''),
        po_number: item.po_number || '',
        delivery_date: item.delivery_date || '',
        bill_to_address: item.bill_to_address || '',
        ship_to_address: item.ship_to_address || '',
        remarks: item.remarks || '',
      });
      setOrderDetails((item.details || []).map(d => ({
        part_id: (d.part_id ? d.part_id.toString() : ''),
        quantity: d.quantity || 0,
        unit_price: d.unit_price || 0,
        total_price: d.total_price || 0,
      })) || []);
    } else {
      setEditingItem(null);
      setSelectedPO(null);
      setFormData({
        order_date: new Date().toISOString().split('T')[0],
        customer_id: '', customer_po_id: '', po_number: '', delivery_date: '',
        bill_to_address: '', ship_to_address: '', remarks: '',
      });
      setOrderDetails([]);
    }
    setIsDialogOpen(true);
  };

  const handlePOChange = (poId) => {
    const po = activePOs.find(p => p.id === parseInt(poId));
    setSelectedPO(po);
    if (po) {
      setFormData({
        ...formData,
        customer_po_id: poId,
        customer_id: (po.customer_id ? po.customer_id.toString() : ''),
        po_number: po.po_number || '',
        delivery_date: po.delivery_date || '',
        bill_to_address: po.bill_to_address || '',
        ship_to_address: po.ship_to_address || '',
      });
      // Auto-populate order details from PO pending items
      setOrderDetails(po.pending_details.map(d => ({
        part_id: d.part_id.toString(),
        quantity: d.pending_quantity,
        unit_price: d.unit_price,
        total_price: d.unit_price * d.pending_quantity,
        max_quantity: d.pending_quantity,
        part_label: `${d.part_no} - ${d.part_name}`,
      })));
    } else {
      setOrderDetails([]);
    }
  };

  const handleDetailChange = (index, field, value) => {
    const updated = [...orderDetails];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'quantity' || field === 'unit_price') {
      const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(updated[index].quantity) || 0;
      const price = field === 'unit_price' ? parseFloat(value) || 0 : parseFloat(updated[index].unit_price) || 0;
      updated[index].total_price = qty * price;
    }
    setOrderDetails(updated);
  };

  const handleRemovePart = (i) => setOrderDetails(orderDetails.filter((_, idx) => idx !== i));
  const calcTotal = () => orderDetails.reduce((s, d) => s + (parseFloat(d.total_price) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.order_date) {
      toast.error('Customer and Order Date are required');
      return;
    }
    if (!editingItem && !formData.customer_po_id) {
      toast.error('Please select a Customer PO');
      return;
    }
    if (!editingItem && (orderDetails.length === 0 || !orderDetails.some(d => d.part_id && d.quantity))) {
      toast.error('At least one part is required');
      return;
    }

    // Client-side inventory validation
    if (!editingItem) {
      for (const detail of orderDetails) {
        if (!detail.part_id || !detail.quantity) continue;
        const available = getPartInventory(parseInt(detail.part_id));
        const qty = parseFloat(detail.quantity);
        if (qty > available) {
          const partName = getPartName(parseInt(detail.part_id));
          toast.error(`Insufficient stock for ${partName}. Available: ${available.toFixed(0)}, Requested: ${qty.toFixed(0)}`);
          return;
        }
        // Validate against PO pending quantity
        if (detail.max_quantity && qty > detail.max_quantity) {
          const partName = getPartName(parseInt(detail.part_id));
          toast.error(`Qty for ${partName} exceeds PO pending: ${detail.max_quantity.toFixed(0)}`);
          return;
        }
      }
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        order_date: formData.order_date,
        customer_id: parseInt(formData.customer_id),
        customer_po_id: formData.customer_po_id ? parseInt(formData.customer_po_id) : null,
        po_number: formData.po_number || null,
        delivery_date: formData.delivery_date || null,
        bill_to_address: formData.bill_to_address || null,
        ship_to_address: formData.ship_to_address || null,
        remarks: formData.remarks || null,
        total_amount: calcTotal(),
        details: orderDetails.filter(d => d.part_id && d.quantity).map(d => ({
          part_id: parseInt(d.part_id),
          quantity: parseFloat(d.quantity),
          unit_price: parseFloat(d.unit_price),
          total_price: parseFloat(d.total_price),
        })),
      };

      if (editingItem) {
        const { details, ...updateData } = submitData;
        await api.put(`/sales-orders/${editingItem.id}`, updateData);
        toast.success('Order updated');
      } else {
        await api.post('/sales-orders', submitData);
        toast.success('Sales Order created');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Delete this sales order?')) return;
    try {
      await api.delete(`/sales-orders/${item.id}`);
      toast.success('Order deleted');
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Delete failed');
    }
  };

  const handleView = async (item) => {
    try {
      const res = await api.get(`/sales-orders/${item.id}`);
      setViewingOrder(res.data);
      setIsViewDialogOpen(true);
    } catch { toast.error('Failed to load order'); }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await api.put(`/sales-orders/${item.id}`, { status: newStatus });
      toast.success('Status updated');
      fetchData();
      setIsViewDialogOpen(false);
    } catch { toast.error('Failed to update status'); }
  };

  const getStatusBadge = (status) => {
    const cfg = ORDER_STATUSES.find(s => s.value === status) || ORDER_STATUSES[0];
    return <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>;
  };

  const getPORef = (poId) => {
    if (!poId) return '-';
    const po = activePOs.find(p => p.id === poId);
    return po ? `CPO-${po.po_no}` : `CPO-${poId}`;
  };

  const fmtCurrency = (v) => parseFloat(v || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return getCustName(item.customer_id).toLowerCase().includes(t) ||
      (item.order_no ? item.order_no.toString() : '').includes(t) ||
      (item.po_number || '').toLowerCase().includes(t) ||
      (item.status || '').toLowerCase().includes(t);
  });

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid="sales-orders">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-sky-500" />
            Sales Orders
          </h1>
          <p className="text-slate-500 text-sm">{data.length} orders | {activePOs.length} active POs available</p>
        </div>
        {canEdit && (
          <Button onClick={() => handleOpenDialog()} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-new-btn"
            disabled={activePOs.length === 0} title={activePOs.length === 0 ? 'No active Customer POs available' : ''}>
            <Plus className="w-4 h-4 mr-2" /> New Sales Order
          </Button>
        )}
      </div>

      <Card className="mb-4 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search Order No, Customer, PO..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 rounded-sm" data-testid="search-input" />
            </div>
            <Button variant="outline" size="icon" onClick={fetchData} className="rounded-sm" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No sales orders found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Order No</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer PO</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>PO Number</TableHead>
                    <TableHead>Delivery</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono font-bold">SO-{item.order_no}</TableCell>
                      <TableCell>{new Date(item.order_date).toLocaleDateString()}</TableCell>
                      <TableCell className="font-mono text-sm">{getPORef(item.customer_po_id)}</TableCell>
                      <TableCell className="text-sm">{getCustName(item.customer_id)}</TableCell>
                      <TableCell>{item.po_number || '-'}</TableCell>
                      <TableCell>{item.delivery_date ? new Date(item.delivery_date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell className="text-right font-mono">{fmtCurrency(item.total_amount)}</TableCell>
                      <TableCell>{getStatusBadge(item.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleView(item)} className="h-8 w-8" data-testid={`view-order-${item.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canEdit && item.status === 'PENDING' && (
                            <>
                              <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(item)} className="h-8 w-8" data-testid={`edit-order-${item.id}`}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(item)} className="h-8 w-8 text-red-600" data-testid={`delete-order-${item.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? `Edit SO-${editingItem.order_no}` : 'New Sales Order (from Customer PO)'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            {/* PO Selection - only for new orders */}
            {!editingItem && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-sm">
                <Label className="form-label font-semibold text-blue-800">Select Customer PO <span className="text-red-500">*</span></Label>
                <Select value={formData.customer_po_id} onValueChange={handlePOChange}>
                  <SelectTrigger className="rounded-sm bg-white mt-1" data-testid="po-select">
                    <SelectValue placeholder="Select an active Customer PO..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {activePOs.map(po => (
                      <SelectItem key={po.id} value={po.id.toString()}>
                        CPO-{po.po_no} | {po.po_number} | {po.customer_name} | {po.pending_details.length} items pending
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {activePOs.length === 0 && (
                  <p className="text-sm text-amber-600 mt-1">No active Customer POs with pending items. Create a Customer PO first.</p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 py-2">
              <div>
                <Label className="form-label">Order Date <span className="text-red-500">*</span></Label>
                <Input type="date" value={formData.order_date} onChange={(e) => setFormData(prev => ({ ...prev, order_date: e.target.value }))}
                  required className="rounded-sm" data-testid="order-date-input" />
              </div>
              <div>
                <Label className="form-label">Customer</Label>
                <Input value={getCustName(parseInt(formData.customer_id))} disabled className="rounded-sm bg-slate-50" data-testid="customer-display" />
              </div>
              <div>
                <Label className="form-label">PO Number</Label>
                <Input value={formData.po_number} disabled className="rounded-sm bg-slate-50" data-testid="po-number-input" />
              </div>
              <div>
                <Label className="form-label">Delivery Date</Label>
                <Input type="date" value={formData.delivery_date} onChange={(e) => setFormData(prev => ({ ...prev, delivery_date: e.target.value }))}
                  className="rounded-sm" data-testid="delivery-date-input" />
              </div>
              <div>
                <Label className="form-label">Bill To</Label>
                <Textarea value={formData.bill_to_address} onChange={(e) => setFormData(prev => ({ ...prev, bill_to_address: e.target.value }))}
                  className="rounded-sm h-16" data-testid="bill-to-input" />
              </div>
              <div>
                <Label className="form-label">Ship To</Label>
                <Textarea value={formData.ship_to_address} onChange={(e) => setFormData(prev => ({ ...prev, ship_to_address: e.target.value }))}
                  className="rounded-sm h-16" data-testid="ship-to-input" />
              </div>
            </div>

            {/* Order Items from PO */}
            {!editingItem && orderDetails.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <h3 className="font-semibold text-slate-900 mb-3">Order Items (from PO)</h3>
                <div className="border rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Part</TableHead>
                        <TableHead>Available Stock</TableHead>
                        <TableHead>PO Pending</TableHead>
                        <TableHead>Order Qty</TableHead>
                        <TableHead>Unit Price</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orderDetails.map((d, i) => {
                        const available = d.part_id ? getPartInventory(parseInt(d.part_id)) : 0;
                        const qty = parseFloat(d.quantity) || 0;
                        const isOverstock = d.part_id && qty > available;
                        const isOverPO = d.max_quantity && qty > d.max_quantity;
                        return (
                          <TableRow key={i}>
                            <TableCell className="text-sm font-medium">{d.part_label || getPartName(parseInt(d.part_id))}</TableCell>
                            <TableCell>
                              <span className={`font-mono text-sm ${available > 0 ? 'text-green-600' : 'text-red-600'}`} data-testid={`avail-stock-${i}`}>
                                {available.toFixed(0)}
                              </span>
                            </TableCell>
                            <TableCell className="font-mono text-orange-600 text-sm">{(d.max_quantity || 0).toFixed(0)}</TableCell>
                            <TableCell>
                              <Input type="number" value={d.quantity} onChange={(e) => handleDetailChange(i, 'quantity', e.target.value)}
                                className={`rounded-sm w-24 ${isOverstock || isOverPO ? 'border-red-500 bg-red-50' : ''}`}
                                data-testid={`qty-input-${i}`} />
                              {isOverstock && <p className="text-red-500 text-xs mt-0.5">Exceeds stock</p>}
                              {isOverPO && !isOverstock && <p className="text-red-500 text-xs mt-0.5">Exceeds PO pending</p>}
                            </TableCell>
                            <TableCell>
                              <Input type="number" step="0.01" value={d.unit_price} onChange={(e) => handleDetailChange(i, 'unit_price', e.target.value)}
                                className="rounded-sm w-28" data-testid={`price-input-${i}`} />
                            </TableCell>
                            <TableCell className="font-mono">{fmtCurrency(d.total_price)}</TableCell>
                            <TableCell>
                              <Button type="button" variant="ghost" size="icon" onClick={() => handleRemovePart(i)} className="text-red-600" data-testid={`remove-part-${i}`}>
                                <X className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow className="bg-slate-50">
                        <TableCell colSpan={5} className="text-right font-semibold">Total:</TableCell>
                        <TableCell className="font-mono font-bold text-lg">{fmtCurrency(calcTotal())}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <div className="mt-4">
              <Label className="form-label">Remarks</Label>
              <Input value={formData.remarks} onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder="Notes..." className="rounded-sm" data-testid="remarks-input" />
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="submit-order-btn">
                {isSubmitting ? 'Saving...' : editingItem ? 'Update' : 'Create Sales Order'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Order Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>SO-{(viewingOrder && viewingOrder.order_no)}</span>
              {viewingOrder && getStatusBadge(viewingOrder.status)}
            </DialogTitle>
          </DialogHeader>
          {viewingOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div><span className="text-sm text-slate-500">Order Date</span><p className="font-medium">{new Date(viewingOrder.order_date).toLocaleDateString()}</p></div>
                <div><span className="text-sm text-slate-500">Customer</span><p className="font-medium">{getCustName(viewingOrder.customer_id)}</p></div>
                <div><span className="text-sm text-slate-500">Customer PO</span><p className="font-medium font-mono">{getPORef(viewingOrder.customer_po_id)}</p></div>
                <div><span className="text-sm text-slate-500">PO Number</span><p className="font-medium">{viewingOrder.po_number || '-'}</p></div>
                <div><span className="text-sm text-slate-500">Delivery Date</span><p className="font-medium">{viewingOrder.delivery_date ? new Date(viewingOrder.delivery_date).toLocaleDateString() : '-'}</p></div>
                <div><span className="text-sm text-slate-500">Total Amount</span><p className="font-mono font-bold text-lg">{fmtCurrency(viewingOrder.total_amount)}</p></div>
              </div>
              <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-sm">
                <div><span className="text-sm text-slate-500">Bill To</span><p className="text-sm">{viewingOrder.bill_to_address || '-'}</p></div>
                <div><span className="text-sm text-slate-500">Ship To</span><p className="text-sm">{viewingOrder.ship_to_address || '-'}</p></div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Order Items ({(viewingOrder.details ? viewingOrder.details.length : 0) || 0})</h4>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Part</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Dispatched</TableHead>
                      <TableHead className="text-right">Pending</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(viewingOrder.details || []).map((d, idx) => {
                      const pending = (parseFloat(d.quantity) || 0) - (parseFloat(d.dispatched_quantity) || 0);
                      return (
                        <TableRow key={idx}>
                          <TableCell>{getPartName(d.part_id)}</TableCell>
                          <TableCell className="text-right font-mono">{parseFloat(d.quantity).toFixed(0)}</TableCell>
                          <TableCell className="text-right font-mono">{fmtCurrency(d.unit_price)}</TableCell>
                          <TableCell className="text-right font-mono">{fmtCurrency(d.total_price)}</TableCell>
                          <TableCell className="text-right font-mono text-green-600">{parseFloat(d.dispatched_quantity || 0).toFixed(0)}</TableCell>
                          <TableCell className="text-right font-mono text-orange-600">{pending.toFixed(0)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {viewingOrder.remarks && (
                <div className="border-t pt-3"><h4 className="font-semibold mb-1">Remarks</h4><p className="text-slate-600">{viewingOrder.remarks}</p></div>
              )}
              {canEdit && !['CANCELLED', 'DELIVERED'].includes(viewingOrder.status) && (
                <div className="border-t pt-4 flex gap-2">
                  {viewingOrder.status === 'PENDING' && (
                    <Button onClick={() => handleStatusChange(viewingOrder, 'CONFIRMED')} className="bg-blue-600 hover:bg-blue-700 rounded-sm" data-testid="confirm-order-btn">
                      Confirm Order
                    </Button>
                  )}
                  {viewingOrder.status === 'DISPATCHED' && (
                    <Button onClick={() => handleStatusChange(viewingOrder, 'DELIVERED')} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="deliver-order-btn">
                      Mark Delivered
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => handleStatusChange(viewingOrder, 'CANCELLED')} className="text-red-600 border-red-300 hover:bg-red-50 rounded-sm" data-testid="cancel-order-btn">
                    Cancel Order
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesOrders;
